package org.vreo.devicehub.features.userAuthentication.presentation.verification

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

import org.vreo.devicehub.features.core.domain.DataError
import org.vreo.devicehub.features.core.domain.onError
import org.vreo.devicehub.features.core.domain.onSuccess
import org.vreo.devicehub.features.core.presentation.toUiText
import org.vreo.devicehub.features.userAuthentication.domain.UserAuthenticationRepository

/**
 * Account verification view model
 *
 * @property repository repository for accessing authentication APIs
 * @param userEmail user email used for verification
 * @param validUntilMs valid until timestamp in milliseconds
 */
class AccountVerificationViewModel(
    private val repository: UserAuthenticationRepository,
    userEmail: String,
    validUntilMs: Long
) : ViewModel() {
    private val _state = MutableStateFlow(
        AccountVerificationState(
            userEmail = userEmail,
            validUntilMs = validUntilMs
        )
    )
    val state = _state.asStateFlow()

    /**
     * On action, handles actions initiated by view
     *
     * @param action action to be handled
     */
    fun onAction(action: AccountVerificationActions) {
        when (action) {
            is AccountVerificationActions.OnClearError -> {
                _state.update {
                    it.copy(
                        error = null
                    )
                }
            }

            is AccountVerificationActions.OnBackNavigationClicked -> {}
            is AccountVerificationActions.OnUpdateVerificationCode -> {
                _state.update {
                    it.copy(
                        verificationCode = action.value
                    )
                }
            }

            is AccountVerificationActions.OnResendVerificationCodeClicked -> {
                resendVerificationCode()
            }

            is AccountVerificationActions.OnVerifyClicked -> {
                verifyCode(action.callback)
            }
        }
    }

    /**
     * Resend verification code for pending user
     */
    private fun resendVerificationCode() = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        try {
            val res = repository.requestNewVerificationCode(_state.value.userEmail)

            res
                .onSuccess { data ->
                    _state.update {
                        it.copy(
                            isLoading = false,
                            validUntilMs = data
                        )
                    }
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            error = error.toUiText(),
                            isLoading = false
                        )
                    }
                }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    error = DataError.Remote.UNKNOWN.toUiText(),
                    isLoading = false
                )
            }
        }
    }

    /**
     * Verify user's account via verification code
     *
     * @param callback callback to be invoked on successful verification
     */
    private fun verifyCode(callback: ((String) -> Unit)? = null) = viewModelScope.launch {
        _state.update {
            it.copy(
                isLoading = true
            )
        }

        try {
            val res = repository.verifyUser(
                username = _state.value.userEmail,
                verificationCode = _state.value.verificationCode
            )
            res
                .onSuccess { data ->
                    _state.update {
                        it.copy(
                            isLoading = false
                        )
                    }
                    callback?.invoke(data)
                }
                .onError { error ->
                    _state.update {
                        it.copy(
                            error = error.toUiText(),
                            isLoading = false
                        )
                    }
                }
        } catch (_: Exception) {
            _state.update {
                it.copy(
                    error = DataError.Remote.UNKNOWN.toUiText(),
                    isLoading = false
                )
            }
        }
    }
}